/**
 * LivePersonForm.js — "Text a Live Person" contact form (kind "live-person-form").
 *
 * Faithful parity with old showContactForm (script.js 4092): first/last name,
 * mobile (auto-formatted), birthdate (MM/DD/YYYY, over-21), help-type select,
 * message, and a required terms checkbox with the full RedChirp legal text +
 * "Read more" toggle. Footer: Send + "Powered by RedChirp".
 *
 * Required-field validation runs here; on success it emits
 * ws-contact-submit { fields }. The Fogarty variant owns the response.
 *
 * NOTE: the actual RedChirp submission needs a backend endpoint that does not
 * exist yet — the variant shows the confirmation client-side for now.
 *
 * message.data: { brand }   // brand name used in the legal text
 */

import { emit } from "../../../core/state.js";

const HELP_OPTIONS = [
  ["visiting", "Visiting"],
  ["purchasing wine", "Purchasing Wine"],
  ["membership", "Membership"],
  ["private events", "Private Events"],
  ["something else", "Something Else"],
];

const TERMS_URL = "https://app.redchirp.com/app/public/legal";

// Old phone formatter: digits → "1 (XXX) XXX-XXXX", US-default leading 1.
function formatPhone(value) {
  let digits = value.replace(/\D/g, "");
  if (digits && !digits.startsWith("1")) digits = "1" + digits;
  digits = digits.slice(0, 11);
  const area = digits.slice(1, 4);
  const prefix = digits.slice(4, 7);
  const line = digits.slice(7, 11);
  let out = digits[0] || "";
  if (area) out += ` (${area}`;
  if (area.length === 3) out += `)`;
  if (prefix) out += ` ${prefix}`;
  if (line) out += `-${line}`;
  return out;
}

// Old DOB formatter: digits → MM/DD/YYYY.
function formatDob(value) {
  const d = value.replace(/\D/g, "").slice(0, 8);
  const parts = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean);
  return parts.join("/");
}

export default class LivePersonForm {
  render(message) {
    const brand = message?.data?.brand || "the winery";

    const form = document.createElement("form");
    form.className = "ws-checkout-form ws-liveperson-form";
    form.noValidate = true;

    const refs = {};
    const field = (name, label, build, { required = true, hint } = {}) => {
      const group = document.createElement("div");
      group.className = "ws-checkout-field";
      const lab = document.createElement("label");
      lab.textContent = label;
      if (required) {
        const star = document.createElement("span");
        star.className = "ws-req";
        star.textContent = " *";
        lab.appendChild(star);
      }
      group.appendChild(lab);
      if (hint) {
        const h = document.createElement("small");
        h.className = "ws-liveperson-hint";
        h.textContent = hint;
        group.appendChild(h);
      }
      const input = build();
      input.name = name;
      input.className = "ws-checkout-input";
      group.appendChild(input);
      const err = document.createElement("small");
      err.className = "ws-checkout-error";
      group.appendChild(err);
      form.appendChild(group);
      refs[name] = { input, err, required };
      return input;
    };

    const text = (ph) => () => {
      const i = document.createElement("input");
      i.type = "text";
      i.placeholder = ph;
      return i;
    };

    field("first_name", "First Name", text("Enter your first name"));
    field("last_name", "Last Name", text("Enter your last name"));
    const mobile = field("mobile", "Mobile Number", () => {
      const i = document.createElement("input");
      i.type = "tel";
      i.inputMode = "tel";
      i.placeholder = "Enter your mobile number";
      return i;
    });
    mobile.addEventListener("input", () => {
      mobile.value = formatPhone(mobile.value);
    });
    const dob = field("dob", "Birthdate (to verify over 21)", text("MM/DD/YYYY"), {
      hint: "e.g. 09/03/1998",
    });
    dob.addEventListener("input", () => {
      dob.value = formatDob(dob.value);
    });
    field("help_type", "What do you need help with?", () => {
      const s = document.createElement("select");
      HELP_OPTIONS.forEach(([value, label]) => {
        const opt = document.createElement("option");
        opt.value = value;
        opt.textContent = label;
        s.appendChild(opt);
      });
      return s;
    });
    field("message", "Message", () => {
      const t = document.createElement("textarea");
      t.rows = 4;
      t.placeholder = "Enter your message";
      t.style.resize = "none";
      return t;
    });

    // Terms: checkbox + "Accept terms and conditions" + collapsible legal text.
    const termsWrap = document.createElement("div");
    termsWrap.className = "ws-liveperson-terms";
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.name = "accept_terms";
    termsWrap.appendChild(cb);

    const termsBody = document.createElement("div");
    const termsHead = document.createElement("p");
    termsHead.className = "ws-terms-head";
    termsHead.textContent = "Accept terms and conditions";
    termsBody.appendChild(termsHead);

    const legal = document.createElement("p");
    legal.className = "ws-terms-text ws-terms-collapsed";
    legal.append(
      document.createTextNode(
        `By submitting this form, you give ${brand} permission to send text messages with info, promotions, and offers to the number you provided, including use of an automated system, even if that number is listed on any do not call list. Consent is not a condition of purchase. Texts are recorded. Message and/or data rates may apply. Use is subject to `
      )
    );
    const termsLink = document.createElement("a");
    termsLink.href = TERMS_URL;
    termsLink.target = "_blank";
    termsLink.rel = "noopener noreferrer";
    termsLink.textContent = "Terms";
    legal.appendChild(termsLink);
    legal.append(document.createTextNode(" with an arbitration clause and a "));
    const privacyLink = document.createElement("a");
    privacyLink.href = TERMS_URL;
    privacyLink.target = "_blank";
    privacyLink.rel = "noopener noreferrer";
    privacyLink.textContent = "Privacy Policy";
    legal.appendChild(privacyLink);
    legal.append(document.createTextNode("."));
    termsBody.appendChild(legal);

    const toggle = document.createElement("span");
    toggle.className = "ws-terms-toggle";
    toggle.textContent = "Read more";
    toggle.addEventListener("click", () => {
      const collapsed = legal.classList.toggle("ws-terms-collapsed");
      toggle.textContent = collapsed ? "Read more" : "Read less";
    });
    termsBody.appendChild(toggle);

    termsWrap.appendChild(termsBody);
    form.appendChild(termsWrap);
    const termsErr = document.createElement("small");
    termsErr.className = "ws-checkout-error";
    form.appendChild(termsErr);

    // Footer: Send + Powered by RedChirp.
    const footer = document.createElement("div");
    footer.className = "ws-liveperson-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    submit.textContent = "Send";
    footer.appendChild(submit);
    const powered = document.createElement("p");
    powered.className = "ws-poweredby";
    powered.textContent = "Powered by RedChirp";
    footer.appendChild(powered);
    form.appendChild(footer);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      let ok = true;
      const fields = {};
      Object.entries(refs).forEach(([name, { input, err, required }]) => {
        const value = (input.value || "").trim();
        fields[name] = value;
        if (required && !value) {
          err.textContent = "This field is required";
          ok = false;
        } else {
          err.textContent = "";
        }
      });
      if (!cb.checked) {
        termsErr.textContent = "Please accept terms and conditions";
        ok = false;
      } else {
        termsErr.textContent = "";
      }
      if (!ok) return;
      fields.accept_terms = true;
      submit.disabled = true;
      submit.textContent = "Sent";
      emit("ws-contact-submit", { fields });
    });

    return form;
  }
}
